import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { typeInfo } from "../../../../../constants/typeInfo";
import {
  applyUnderline,
  normalizeEnglishAnswer,
} from "../../../../../utils/textUtils";

import CommonPage from "../../common/CommonPage";

const DescriptiveConditionPage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();

  const [userInput, setUserInput] = useState("");
  const [overlayVisible, setOverlayVisible] = useState(false);

  useEffect(() => {
    if (currentPage.isSubmitted) {
      setUserInput(currentPage.answer);
    } else {
      setUserInput(currentPage.input || "");
    }
  }, [currentIndex]);

  const handleChange = (e) => {
    setUserInput(e.target.value);
  };

  // <보기> 낱말을 누르면 답 상자 끝에 붙여 준다. 손으로 옮겨 적다 철자를 틀리는
  // 일이 많아서 넣었다. 이미 적혀 있는 낱말을 다시 누르면 도로 뺀다.
  const handleExampleChipClick = (word) => {
    if (currentPage.isSubmitted) return;
    const wordList = userInput.split(/\s+/).filter(Boolean);
    const foundAt = wordList.findIndex(
      (one) => one.toLowerCase() === word.toLowerCase()
    );
    if (foundAt >= 0) {
      wordList.splice(foundAt, 1);
      setUserInput(wordList.join(" "));
      return;
    }
    setUserInput(wordList.concat(word).join(" "));
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;

    const correct =
      normalizeEnglishAnswer(userInput) ===
      normalizeEnglishAnswer(currentPage.answer);
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          input: userInput,
          isSubmitted: true,
          isCorrect: correct,
        };
      }
      return page;
    });

    setOverlayVisible(true);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setTimeout(() => {
      setOverlayVisible(false);
      setUserInput(currentPage.answer);
    }, 600);
  };

  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768; // [모의고사 리디자인]
  // 조건문에서 "15 단어" 같은 목표 단어 수를 읽어 카운터에 "N / 15 단어"로 대조 표시
  const conditionText = String(currentPage.text2 ?? currentPage.condition ?? "");
  const targetWordsMatch = conditionText.match(/(\d+)\s*단어/);
  const targetWords = targetWordsMatch ? parseInt(targetWordsMatch[1], 10) : null;
  const wordCount = userInput.trim().split(/\s+/).filter((w) => w.length > 0).length;
  // 보기: 슬래시 나열이면 칩으로 (스캔 속도) — 아니면 원문 그대로
  const exampleText = String(currentPage.text3 ?? currentPage.example ?? "");
  const exampleChips = exampleText.includes("/")
    ? exampleText.split("/").map((w) => w.trim()).filter(Boolean)
    : null;

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      paragraph={currentPage.text || currentPage.paragraph}
      accentColor={typeInfo.paragraph.color}
      overlayVisible={overlayVisible}
      isCorrect={currentPage.isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobileW ? "12px" : "18px",
          // [여백 수술] 폰은 화면 기준 폭 — 조상 요소가 좁혀도 면역
          width: isMobileW ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "100%",
          maxWidth: "680px", // [모의고사 리디자인] 다른 유형과 동일 중앙 열
          margin: "0 auto",
          boxSizing: "border-box",
          padding: isMobileW ? "0 4px" : 0,
        }}
      >
        {/* Q칩 + 카운터 + 진행바 — 다른 모고 유형과 동일 문법 (누락분 보강) */}
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: isMobileW ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
              Q{currentIndex + 1}
            </span>
            <span style={{ marginLeft: "auto", fontSize: isMobileW ? "12px" : "13px", color: "#7A8490", fontWeight: 700 }}>
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.paragraph.color, borderRadius: "999px", transition: "width .25s" }} />
          </div>
        </div>
        <div
          style={{
            width: "100%",
            fontSize: isMobileW ? "16px" : "19px", // [폰트 수정] 폰 거대 폰트 → 반응형
            fontWeight: 600,
            lineHeight: 1.75,
            textAlign: "left",
            wordBreak: "keep-all",
          }}
        >
          {/* 번호는 Q칩이 담당 — 문장 앞 "N." 표시만 제거(데이터 무변경) */}
          {applyUnderline(
            String(currentPage.question || "").replace(/^\s*\d+\.\s*/, "")
          )}
        </div>
        {/* 내용이 없으면 상자를 아예 그리지 않는다. 제목만 있는 빈 상자가 떠서
            아이가 무엇을 놓친 줄 안다 (2026-09-13 신고) */}
        {conditionText && (
        <div
          style={{
            boxSizing: "border-box",
            fontSize: isMobileW ? "14px" : "16px",
            lineHeight: "1.7",
            width: "90%",
            padding: isMobileW ? "11px 13px" : "12px 18px",
            border: "1px solid #E6EDEC", // [모의고사 리디자인] 카드 스킨
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <span style={{ color: typeInfo.paragraph.color, fontWeight: 800, fontSize: isMobileW ? "12px" : "13px", letterSpacing: "0.06em" }}>
            {(currentPage.text2_heading || "<조건>").replace(/[<>]/g, "")}
          </span>
          <br />
          {String(currentPage.text2 ?? currentPage.condition ?? "")
            .split("\n")
            .map((line, index) => (
              <React.Fragment key={index}>
                {line} <br />
              </React.Fragment>
            ))}
        </div>
        )}
        {exampleText && (
        <div
          style={{
            boxSizing: "border-box",
            fontSize: isMobileW ? "14px" : "16px",
            lineHeight: "1.7",
            width: "90%",
            padding: isMobileW ? "11px 13px" : "12px 18px",
            border: "1px solid #E6EDEC",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <span style={{ color: typeInfo.paragraph.color, fontWeight: 800, fontSize: isMobileW ? "12px" : "13px", letterSpacing: "0.06em" }}>
            {(currentPage.text3_heading || "<보기>").replace(/[<>]/g, "")}
          </span>
          <br />
          {/* [모의고사 리디자인] 슬래시 나열 → 단어 칩 (아닌 형식은 원문 유지) */}
          {exampleChips
            ? exampleChips.map((w, i) => {
                const isUsed = userInput
                  .split(/\s+/)
                  .filter(Boolean)
                  .some((one) => one.toLowerCase() === w.toLowerCase());
                return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleExampleChipClick(w)}
                  disabled={currentPage.isSubmitted}
                  style={{
                    display: "inline-block",
                    backgroundColor: isUsed ? typeInfo.paragraph.color : "#F4F8F7",
                    color: isUsed ? "#FFFFFF" : "inherit",
                    border: "none",
                    borderRadius: "7px",
                    padding: "3px 10px",
                    margin: "0 4px 5px 0",
                    fontFamily: "inherit",
                    fontWeight: 600,
                    fontSize: isMobileW ? "13px" : "14px",
                    cursor: currentPage.isSubmitted ? "default" : "pointer",
                  }}
                >
                  {w}
                </button>
                );
              })
            : (currentPage.text3 ?? currentPage.example)}
        </div>
        )}
        <div style={{ position: "relative", width: "90%", marginTop: "10px", marginBottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "48px" : 0 }}>
          <textarea
            value={userInput}
            onChange={handleChange}
            disabled={currentPage.isSubmitted}
            rows={isMobileW ? 3 : 2}
            placeholder="답을 입력해주세요."
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: isMobileW ? "10px 12px" : "10px 13px",
              resize: "vertical",
              fontFamily: "inherit",
              fontSize: isMobileW ? "15px" : "16px",
              lineHeight: "1.6",
              borderRadius: "12px",
              border: `1px solid ${typeInfo.paragraph.color}`,
              backgroundColor: currentPage.isSubmitted
                ? typeInfo.paragraph.backgroundColor
                : "#FFFFFF",
            }}
          />
          <div
            style={{
              marginTop: "6px",
              fontSize: isMobileW ? "13px" : "14px",
              fontWeight: 800,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {/* [모의고사 리디자인] 조건의 목표 단어 수와 즉시 대조 — 도달 시 teal */}
            <span
              style={{
                color:
                  targetWords !== null && wordCount === targetWords
                    ? "#00A396"
                    : typeInfo.paragraph.color,
              }}
            >
              {wordCount}
            </span>
            <span style={{ color: "#7A8490" }}>
              {targetWords !== null ? ` / ${targetWords} 단어` : " 단어"}
            </span>
          </div>
        </div>
      </div>
    </CommonPage>
  );
};

export default DescriptiveConditionPage;
